import { createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Warehouse, Building2, ChevronRight, Loader2, PackageSearch, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';

/**
 * WMS drill-down: Warehouse → Merchant → Modules.
 *
 * Every WMS screen is really a question about ONE merchant's goods in ONE building, but the flat
 * menu asked that question again on every screen — each tab carried its own merchant and warehouse
 * pickers, and nothing stopped two tabs disagreeing. Choosing once, up front, is what makes the
 * numbers on Inventory, Inbound and Reports belong to the same thing.
 *
 * The choice lives in the URL (`/wms/w/:warehouseId/m/:merchantId/<section>`) rather than in state,
 * so a screen can be linked, bookmarked and reloaded without silently reverting to someone else's
 * stock.
 */

export interface WmsScopeValue {
  warehouseId: number | null;
  merchantId: number | null;
  warehouseName?: string;
  merchantName?: string;
}

export const WmsScopeContext = createContext<WmsScopeValue>({ warehouseId: null, merchantId: null });

export function useWmsScope() {
  return useContext(WmsScopeContext);
}

export interface WmsWarehouseLite { id: number; code?: string; name: string; city?: string; status?: string; isActive?: boolean }
export interface WmsMerchantLite {
  id: number; companyName: string; brandName?: string | null; wmsEnabled?: boolean;
  allocated?: boolean; stockUnits?: number; grnCount?: number; source?: 'ALLOCATION' | 'STOCK' | 'GRN';
}

export function useWmsWarehouses() {
  return useQuery<WmsWarehouseLite[]>({
    queryKey: ['wms-warehouses-picker'],
    queryFn: async () => (await api.get('/wms/warehouses')).data as WmsWarehouseLite[],
  });
}

export function useWarehouseMerchants(warehouseId?: number | null) {
  return useQuery<WmsMerchantLite[]>({
    queryKey: ['wms-warehouse-merchants', warehouseId],
    enabled: !!warehouseId,
    queryFn: async () => (await api.get(`/wms/warehouses/${warehouseId}/merchants`)).data as WmsMerchantLite[],
  });
}

/* ─────────────────────────── Step 1: pick a warehouse ─────────────────────────── */

export function WarehousePicker() {
  const nav = useNavigate();
  const { data: warehouses = [], isLoading } = useWmsWarehouses();

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Warehouse size={22} className="text-primary" /> Warehouses
          </h1>
          <p className="text-sm text-slate-500 mt-1 mb-5">Pick a warehouse to see the merchants stored in it.</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button onClick={() => nav('/wms/warehouses')} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs hover:bg-slate-50">Manage warehouses</button>
          <button onClick={() => nav('/wms/masters/merchants')} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs hover:bg-slate-50">Merchant allocations</button>
        </div>
      </div>

      {isLoading && <div className="py-16 text-center"><Loader2 size={22} className="mx-auto animate-spin text-primary" /></div>}
      {!isLoading && warehouses.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400">No warehouses configured yet.</div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {warehouses.map((w) => (
          <button key={w.id} onClick={() => nav(`/wms/w/${w.id}`)}
            className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-primary hover:shadow-sm transition group">
            <div className="flex items-start justify-between gap-2">
              <Warehouse size={18} className="text-primary shrink-0 mt-0.5" />
              <ChevronRight size={16} className="text-slate-300 group-hover:text-primary" />
            </div>
            <div className="font-semibold text-slate-800 mt-2 leading-snug">{w.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{[w.city, w.code].filter(Boolean).join(' · ')}</div>
            {w.status && w.status !== 'ACTIVE' && (
              <span className="inline-block mt-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{w.status}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── Step 2: pick a merchant ─────────────────────────── */

export function MerchantPicker({ warehouseId }: { warehouseId: number }) {
  const nav = useNavigate();
  const { data: warehouses = [] } = useWmsWarehouses();
  const wh = warehouses.find((w) => w.id === warehouseId);
  const { data: merchants = [], isLoading } = useWarehouseMerchants(warehouseId);

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <button onClick={() => nav('/wms')} className="text-sm text-slate-500 hover:text-slate-900 mb-2">← All warehouses</button>
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
        <Warehouse size={22} className="text-primary" /> {wh?.name ?? `Warehouse #${warehouseId}`}
      </h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">Pick a merchant to open their inventory, inbound, outbound, returns and reports in this warehouse.</p>

      {isLoading && <div className="py-16 text-center"><Loader2 size={22} className="mx-auto animate-spin text-primary" /></div>}

      {!isLoading && merchants.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
          <div className="font-medium flex items-center gap-1.5 mb-1"><AlertTriangle size={15} /> No merchants in this warehouse yet</div>
          A merchant appears here once they have a space allocation, stock on the floor, or a GRN received here.
          Assign one under <b>WMS → Masters → Merchants</b>.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {merchants.map((m) => (
          <button key={m.id} onClick={() => nav(`/wms/w/${warehouseId}/m/${m.id}/dashboard`)}
            className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-primary hover:shadow-sm transition group">
            <div className="flex items-start justify-between gap-2">
              <Building2 size={18} className="text-primary shrink-0 mt-0.5" />
              <ChevronRight size={16} className="text-slate-300 group-hover:text-primary" />
            </div>
            <div className="font-semibold text-slate-800 mt-2 leading-snug">{m.brandName || m.companyName}</div>
            <div className="text-xs text-slate-500 mt-0.5">#{m.id}{m.brandName ? ` · ${m.companyName}` : ''}</div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[11px]">
              {m.allocated
                ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Allocated</span>
                // Worth saying out loud: goods are here for a merchant nobody formally allocated space to.
                : <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title="No space allocation — listed because stock or a GRN exists here">Not allocated</span>}
              <span className="text-slate-500 flex items-center gap-1"><PackageSearch size={12} /> {m.stockUnits ?? 0} units</span>
              {!!m.grnCount && <span className="text-slate-400">{m.grnCount} GRN</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── Warehouse-level admin bar ─────────────────────────── */

/** Settings for the BUILDING rather than for one merchant's goods — reached from the picker. */
export function WmsAdminBar({ active }: { active: string }) {
  const nav = useNavigate();
  const items = [
    { label: 'Warehouses', section: 'warehouses' },
    { label: 'Merchant Allocations', section: 'masters/merchants' },
    { label: 'Suppliers', section: 'masters/suppliers' },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
      <button onClick={() => nav('/wms')} className="text-slate-500 hover:text-primary flex items-center gap-1.5">
        <Warehouse size={14} /> Warehouses
      </button>
      <ChevronRight size={14} className="text-slate-300" />
      <span className="text-slate-400 text-xs mr-1">Setup</span>
      {items.map((it) => (
        <button key={it.section} onClick={() => nav(`/wms/${it.section}`)}
          className={`px-2 py-1 rounded text-xs ${active === it.section ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
          {it.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────── Module menu, inside the merchant ─────────────────────────── */

/**
 * The modules, grouped the way the work runs: what is here, what came in, what went out, what came
 * back, and what it all adds up to.
 *
 * `warehouses` and the merchant allocation master are deliberately NOT here — they are warehouse-
 * level settings, not one merchant's goods, and they stay on the warehouse step above.
 */
export const WMS_MODULES: { name: string; items: { label: string; section: string }[] }[] = [
  { name: 'Overview', items: [{ label: 'Dashboard', section: 'dashboard' }] },
  {
    name: 'Inventory', items: [
      { label: 'Stock', section: 'inventory' },
      { label: 'Products', section: 'products' },
      { label: 'Stock Transfer', section: 'stock-transfer' },
      { label: 'Cycle Count', section: 'cycle-count' },
      { label: 'Stock Adjustment', section: 'stock-adjustment' },
    ],
  },
  {
    name: 'Inbound', items: [
      { label: 'GRN', section: 'inbound/grn' },
      { label: 'Put Away', section: 'inbound/putaway' },
      { label: 'QC', section: 'inbound/qc' },
    ],
  },
  {
    name: 'Outbound', items: [
      { label: 'Orders', section: 'outbound/orders' },
      { label: 'Dispatch', section: 'outbound/dispatch' },
      { label: 'Label Printing', section: 'outbound/labels' },
      { label: 'Dispatch Register', section: 'outbound/register' },
    ],
  },
  {
    name: 'Returns', items: [
      { label: 'RTO Inward', section: 'returns/rto-inward' },
      { label: 'QC', section: 'returns/qc' },
      { label: 'Restock', section: 'returns/restock' },
    ],
  },
  {
    name: 'Reports', items: [
      { label: 'Stock', section: 'reports/stock' },
      { label: 'Inventory Ageing', section: 'reports/ageing' },
      { label: 'Dispatch', section: 'reports/dispatch' },
      { label: 'Merchant', section: 'reports/merchant' },
      { label: 'Space Utilisation', section: 'reports/space' },
      { label: 'Billing', section: 'reports/billing' },
    ],
  },
];

export function WmsModuleNav({ warehouseId, merchantId, active }: { warehouseId: number; merchantId: number; active: string }) {
  const nav = useNavigate();
  const base = `/wms/w/${warehouseId}/m/${merchantId}`;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-x-5 gap-y-2">
      {WMS_MODULES.map((g) => (
        <div key={g.name} className="min-w-[120px]">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{g.name}</div>
          <div className="flex flex-wrap gap-1">
            {g.items.map((it) => {
              const on = active === it.section;
              return (
                <button key={it.section} onClick={() => nav(`${base}/${it.section}`)}
                  className={`px-2 py-1 rounded text-xs transition ${
                    on ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}>
                  {it.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────── Breadcrumb shown on every scoped screen ─────────────────────────── */

export function WmsScopeBar({ warehouseId, merchantId }: { warehouseId: number; merchantId: number }) {
  const nav = useNavigate();
  const { data: warehouses = [] } = useWmsWarehouses();
  const { data: merchants = [] } = useWarehouseMerchants(warehouseId);
  const wh = warehouses.find((w) => w.id === warehouseId);
  const m = merchants.find((x) => x.id === merchantId);

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 flex flex-wrap items-center gap-1.5 text-sm">
      <button onClick={() => nav('/wms')} className="text-slate-500 hover:text-primary flex items-center gap-1.5">
        <Warehouse size={14} /> Warehouses
      </button>
      <ChevronRight size={14} className="text-slate-300" />
      <button onClick={() => nav(`/wms/w/${warehouseId}`)} className="text-slate-600 hover:text-primary font-medium">
        {wh?.name ?? `#${warehouseId}`}
      </button>
      <ChevronRight size={14} className="text-slate-300" />
      <span className="text-slate-900 font-semibold flex items-center gap-1.5">
        <Building2 size={14} className="text-primary" /> {m ? (m.brandName || m.companyName) : `Merchant #${merchantId}`}
      </span>
      <button onClick={() => nav(`/wms/w/${warehouseId}`)} className="ml-auto text-xs text-slate-400 hover:text-primary underline">
        Change merchant
      </button>
    </div>
  );
}
